import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StoryHistory, StoryTurn, TextPatch } from '../templateGen/templateInterfaces';
import { StoryPatchService } from './story-patch.service';

@Injectable({ providedIn: 'root' })
export class StoryHistoryService {

  private historySubject = new BehaviorSubject<StoryHistory | null>(null);
  readonly history$: Observable<StoryHistory | null> = this.historySubject.asObservable();

  constructor(private patchService: StoryPatchService) {}

  /**
   * Initialize a new history with the original story (the "bible").
   */
  initialize(originalStory: string): void {
    this.historySubject.next({
      original_story: originalStory,
      turns: [],
      current_turn: -1,
      concorded_story: originalStory,
    });
  }

  /**
   * Add a new turn with patches applied.
   * Produces the concorded_story by applying all cumulative patches.
   */
  addTurn(
    patches: TextPatch[],
    action: StoryTurn['action'],
    summary: string,
  ): StoryHistory | null {
    const current = this.historySubject.value;
    if (!current) return null;

    const turnNumber = current.turns.length;
    const prevCumulative = turnNumber > 0
      ? current.turns[turnNumber - 1].patches_cumulative
      : [];

    const cumulative = [...prevCumulative, ...patches];
    const concordedStory = this.patchService.applyCumulativePatches(
      current.original_story, cumulative,
    );

    const turn: StoryTurn = {
      turn_number: turnNumber,
      timestamp: new Date().toISOString(),
      story_snapshot: concordedStory,
      patches_applied: patches,
      patches_cumulative: cumulative,
      action,
      summary,
    };

    const updated: StoryHistory = {
      ...current,
      turns: [...current.turns, turn],
      current_turn: turnNumber,
      concorded_story: concordedStory,
    };

    this.historySubject.next(updated);
    return updated;
  }

  /**
   * Navigate to a specific turn. Updates concorded_story to that turn's snapshot.
   */
  navigateToTurn(turnNumber: number): void {
    const current = this.historySubject.value;
    if (!current) return;

    if (turnNumber < 0) {
      // Turn -1 = original story (before any patches)
      this.historySubject.next({
        ...current,
        current_turn: -1,
        concorded_story: current.original_story,
      });
      return;
    }

    if (turnNumber >= current.turns.length) return;

    this.historySubject.next({
      ...current,
      current_turn: turnNumber,
      concorded_story: current.turns[turnNumber].story_snapshot,
    });
  }

  /**
   * Go to next turn.
   */
  next(): void {
    const current = this.historySubject.value;
    if (!current) return;
    if (current.current_turn < current.turns.length - 1) {
      this.navigateToTurn(current.current_turn + 1);
    }
  }

  /**
   * Go to previous turn.
   */
  previous(): void {
    const current = this.historySubject.value;
    if (!current) return;
    if (current.current_turn > -1) {
      this.navigateToTurn(current.current_turn - 1);
    }
  }

  /**
   * Get the current history snapshot (for persistence).
   */
  getSnapshot(): StoryHistory | null {
    return this.historySubject.value;
  }

  /**
   * Restore history from a persisted snapshot (session load).
   */
  restore(history: StoryHistory): void {
    this.historySubject.next(history);
  }

  /**
   * Reset history (new session).
   */
  reset(): void {
    this.historySubject.next(null);
  }

  /**
   * Get the original story (the "bible").
   */
  getOriginalStory(): string {
    return this.historySubject.value?.original_story || '';
  }

  /**
   * Get the concorded story at the current turn.
   */
  getConcordedStory(): string {
    return this.historySubject.value?.concorded_story || '';
  }

  /**
   * Get total number of turns.
   */
  getTurnCount(): number {
    return this.historySubject.value?.turns.length || 0;
  }

  /**
   * Get patches at a specific turn for diff display.
   */
  getPatchesAtTurn(turnNumber: number): TextPatch[] {
    const current = this.historySubject.value;
    if (!current || turnNumber < 0 || turnNumber >= current.turns.length) return [];
    return current.turns[turnNumber].patches_applied;
  }
}
